import React, { useMemo, useState } from 'react';
import { getImageUrl } from '../../services/imageService';
import './ProductPicker.css';

const getThumb = (product) => {
  const image =
    product.images?.find((img) => img.is_primary) || product.images?.[0];
  return image?.image_url ? getImageUrl(image.image_url) : null;
};

/**
 * Hand-picks the products in a campaign and fixes their order.
 *
 * Order is edited with ↑/↓ rather than drag-and-drop: nothing in this admin
 * uses drag-and-drop, and buttons stay usable from the keyboard.
 */
const ProductPicker = ({ products = [], selectedIds = [], onChange }) => {
  const [query, setQuery] = useState('');

  const byId = useMemo(() => {
    const map = new Map();
    products.forEach((product) => map.set(product.id, product));
    return map;
  }, [products]);

  const selected = useMemo(
    () => selectedIds.map((id) => byId.get(id)).filter(Boolean),
    [selectedIds, byId]
  );

  const available = useMemo(() => {
    const term = query.trim().toLowerCase();
    return products.filter((product) => {
      if (selectedIds.includes(product.id)) return false;
      if (!term) return true;
      return (
        product.name?.toLowerCase().includes(term) ||
        product.sku?.toLowerCase().includes(term)
      );
    });
  }, [products, selectedIds, query]);

  const add = (id) => onChange([...selectedIds, id]);
  const remove = (id) => onChange(selectedIds.filter((sid) => sid !== id));

  const move = (index, delta) => {
    const next = [...selectedIds];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  return (
    <div className="product-picker">
      <div className="product-picker-col">
        <div className="product-picker-head">
          <h3>Available products</h3>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or SKU"
            className="product-picker-search"
          />
        </div>

        <ul className="product-picker-list">
          {available.length === 0 && (
            <li className="product-picker-empty">
              {query ? 'No products match that search.' : 'All products selected.'}
            </li>
          )}
          {available.map((product) => {
            const thumb = getThumb(product);
            return (
              <li key={product.id} className="product-picker-row">
                {thumb ? (
                  <img src={thumb} alt="" className="product-picker-thumb" />
                ) : (
                  <div className="product-picker-thumb product-picker-thumb--empty" />
                )}
                <div className="product-picker-meta">
                  <span className="product-picker-name">{product.name}</span>
                  {product.sku && (
                    <span className="product-picker-sku">{product.sku}</span>
                  )}
                </div>
                <button
                  type="button"
                  className="product-picker-btn"
                  onClick={() => add(product.id)}
                  aria-label={`Add ${product.name}`}
                >
                  Add
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="product-picker-col">
        <div className="product-picker-head">
          <h3>
            In this campaign{' '}
            <span className="product-picker-count">({selected.length})</span>
          </h3>
          <p className="product-picker-hint">
            This order is the order shoppers see.
          </p>
        </div>

        <ul className="product-picker-list">
          {selected.length === 0 && (
            <li className="product-picker-empty">
              No products picked yet. Add some from the left.
            </li>
          )}
          {selected.map((product, index) => {
            const thumb = getThumb(product);
            return (
              <li key={product.id} className="product-picker-row">
                <span className="product-picker-index">{index + 1}</span>
                {thumb ? (
                  <img src={thumb} alt="" className="product-picker-thumb" />
                ) : (
                  <div className="product-picker-thumb product-picker-thumb--empty" />
                )}
                <div className="product-picker-meta">
                  <span className="product-picker-name">{product.name}</span>
                  {product.sku && (
                    <span className="product-picker-sku">{product.sku}</span>
                  )}
                </div>
                <div className="product-picker-actions">
                  <button
                    type="button"
                    className="product-picker-btn product-picker-btn--icon"
                    onClick={() => move(index, -1)}
                    disabled={index === 0}
                    aria-label={`Move ${product.name} up`}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="product-picker-btn product-picker-btn--icon"
                    onClick={() => move(index, 1)}
                    disabled={index === selected.length - 1}
                    aria-label={`Move ${product.name} down`}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="product-picker-btn product-picker-btn--remove"
                    onClick={() => remove(product.id)}
                    aria-label={`Remove ${product.name}`}
                  >
                    ×
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default ProductPicker;
